import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Radio,
  Result,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { SendOutlined } from '@ant-design/icons';

import api from '../services/api';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

interface BroadcastResult {
  total: number;
  success: number;
  failed: number;
  channel: string;
  record_id: number;
}

interface BroadcastRecordItem {
  id: number;
  message_preview: string;
  message?: string | null;
  target: 'all' | 'active_shops';
  channel: 'main_bot' | 'merchant_bot';
  total_count: number;
  success_count: number;
  failed_count: number;
  initiated_by_username?: string | null;
  created_at?: string | null;
  completed_at?: string | null;
}

interface BroadcastHistoryResponse {
  items: BroadcastRecordItem[];
  total: number;
  page: number;
  pageSize: number;
}

interface BroadcastRecipientLogItem {
  id: number;
  user_id?: number | null;
  telegram_id: number;
  username?: string | null;
  recipient_name?: string | null;
  delivery_status: 'success' | 'failed';
  channel_used: 'main_bot' | 'merchant_bot' | 'main_bot_fallback' | 'unavailable';
  error_message?: string | null;
  sent_at?: string | null;
}

interface BroadcastHistoryDetailResponse {
  record: BroadcastRecordItem;
  items: BroadcastRecipientLogItem[];
  total: number;
  page: number;
  pageSize: number;
}

const formatDateText = (value?: string | null) => {
  if (!value) return '-';
  return value.replace('T', ' ').replace(/-/g, '/').slice(0, 19);
};

const renderTargetLabel = (value: BroadcastRecordItem['target']) =>
  value === 'all' ? '全部用户' : '已开店商户';

const renderChannelLabel = (value: string) => {
  if (value === 'merchant_bot') return '商户Bot';
  if (value === 'main_bot_fallback') return '主机器人回退';
  if (value === 'unavailable') return '无可用通道';
  return '主机器人';
};

const renderStatusTag = (value: BroadcastRecipientLogItem['delivery_status']) => (
  <Tag color={value === 'success' ? 'green' : 'red'}>{value === 'success' ? '成功' : '失败'}</Tag>
);

const Broadcast: React.FC = () => {
  const [target, setTarget] = useState<'all' | 'active_shops'>('active_shops');
  const [msg, setMsg] = useState('');
  const [useMerchantBot, setUseMerchantBot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BroadcastResult | null>(null);

  const [recordsLoading, setRecordsLoading] = useState(false);
  const [records, setRecords] = useState<BroadcastRecordItem[]>([]);
  const [recordPage, setRecordPage] = useState(1);
  const [recordPageSize, setRecordPageSize] = useState(10);
  const [recordTotal, setRecordTotal] = useState(0);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeRecordId, setActiveRecordId] = useState<number | null>(null);
  const [detailRecord, setDetailRecord] = useState<BroadcastRecordItem | null>(null);
  const [detailItems, setDetailItems] = useState<BroadcastRecipientLogItem[]>([]);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(10);
  const [detailTotal, setDetailTotal] = useState(0);

  const loadRecords = async (page = recordPage, pageSize = recordPageSize) => {
    setRecordsLoading(true);
    try {
      const res = (await api.get('/broadcast/records', {
        params: { page, pageSize },
      })) as BroadcastHistoryResponse;
      setRecords(res.items || []);
      setRecordTotal(res.total || 0);
    } catch (error) {
      setRecords([]);
      setRecordTotal(0);
    } finally {
      setRecordsLoading(false);
    }
  };

  const loadDetail = async (recordId: number, page = detailPage, pageSize = detailPageSize) => {
    setDetailLoading(true);
    try {
      const res = (await api.get(`/broadcast/records/${recordId}`, {
        params: { page, pageSize },
      })) as BroadcastHistoryDetailResponse;
      setDetailRecord(res.record);
      setDetailItems(res.items || []);
      setDetailTotal(res.total || 0);
    } catch (error) {
      setDetailRecord(null);
      setDetailItems([]);
      setDetailTotal(0);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    void loadRecords(recordPage, recordPageSize);
  }, [recordPage, recordPageSize]);

  useEffect(() => {
    if (!detailOpen || !activeRecordId) return;
    void loadDetail(activeRecordId, detailPage, detailPageSize);
  }, [detailOpen, activeRecordId, detailPage, detailPageSize]);

  const handleSend = async () => {
    if (!msg.trim()) {
      message.warning('请输入消息内容');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = (await api.post('/broadcast/send', {
        message: msg.trim(),
        target,
        use_merchant_bot: useMerchantBot,
      })) as BroadcastResult;
      setResult(res);
      message.success(`发送完成：成功 ${res.success} 条，失败 ${res.failed} 条`);
      setRecordPage(1);
      void loadRecords(1, recordPageSize);
      if (res.record_id) {
        setActiveRecordId(res.record_id);
        setDetailPage(1);
        setDetailPageSize(10);
        setDetailOpen(true);
      }
    } catch (e: any) {
      message.error(e.response?.data?.detail || '发送失败');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetail = (recordId: number) => {
    setActiveRecordId(recordId);
    setDetailPage(1);
    setDetailPageSize(10);
    setDetailOpen(true);
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card title="消息广播">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text strong>推送目标</Text>
              <div style={{ marginTop: 8 }}>
                <Radio.Group value={target} onChange={(e) => setTarget(e.target.value)}>
                  <Radio.Button value="active_shops">已开店商户</Radio.Button>
                  <Radio.Button value="all">全部用户</Radio.Button>
                </Radio.Group>
              </div>
            </div>

            <div>
              <Text strong>发送通道</Text>
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Switch checked={useMerchantBot} onChange={setUseMerchantBot} />
                <Text>{useMerchantBot ? '通过商户自己的Bot发送' : '通过主机器人发送'}</Text>
              </div>
              {useMerchantBot && (
                <Alert
                  message="商户Bot通道：优先通过每个商户自己的机器人发送；若商户没有可用机器人，将自动回退到主机器人。"
                  type="info"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </div>

            <div>
              <Text strong>消息内容</Text>
              <TextArea
                rows={6}
                maxLength={4096}
                showCount
                placeholder="请输入广播消息内容..."
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                style={{ marginTop: 8 }}
              />
            </div>

            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={loading}
              onClick={handleSend}
              disabled={!msg.trim()}
            >
              发送广播
            </Button>

            {result && (
              <Result
                status={result.failed === 0 ? 'success' : 'warning'}
                title="发送完成"
                subTitle={`共 ${result.total} 条，成功 ${result.success} 条，失败 ${result.failed} 条（通道：${renderChannelLabel(result.channel)}）`}
              />
            )}
          </Space>
        </Card>

        <Card title="发送记录">
          <Table<BroadcastRecordItem>
            rowKey="id"
            loading={recordsLoading}
            dataSource={records}
            pagination={{
              current: recordPage,
              pageSize: recordPageSize,
              total: recordTotal,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              onChange: (page, pageSize) => {
                setRecordPage(page);
                setRecordPageSize(pageSize);
              },
            }}
            columns={[
              {
                title: '发送时间',
                dataIndex: 'created_at',
                width: 180,
                render: (value: string | null) => formatDateText(value),
              },
              {
                title: '消息预览',
                dataIndex: 'message_preview',
                ellipsis: true,
              },
              {
                title: '目标',
                dataIndex: 'target',
                width: 120,
                render: (value: BroadcastRecordItem['target']) => <Tag>{renderTargetLabel(value)}</Tag>,
              },
              {
                title: '通道',
                dataIndex: 'channel',
                width: 140,
                render: (value: string) => <Tag color="blue">{renderChannelLabel(value)}</Tag>,
              },
              {
                title: '结果',
                width: 160,
                render: (_, record) => (
                  <Space size={4}>
                    <Tag color="green">成功 {record.success_count}</Tag>
                    <Tag color="red">失败 {record.failed_count}</Tag>
                  </Space>
                ),
              },
              {
                title: '发起人',
                dataIndex: 'initiated_by_username',
                width: 120,
                render: (value: string | null) => value || '-',
              },
              {
                title: '操作',
                width: 120,
                render: (_, record) => (
                  <Button type="link" onClick={() => handleOpenDetail(record.id)}>
                    查看详情
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      </Space>

      <Drawer
        title="广播详情"
        open={detailOpen}
        width={980}
        onClose={() => setDetailOpen(false)}
        destroyOnClose={false}
      >
        {!detailRecord && !detailLoading ? (
          <Empty description="暂无详情数据" />
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="记录ID">{detailRecord?.id ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="发起人">{detailRecord?.initiated_by_username || '-'}</Descriptions.Item>
              <Descriptions.Item label="发送目标">
                {detailRecord ? renderTargetLabel(detailRecord.target) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="发送通道">
                {detailRecord ? renderChannelLabel(detailRecord.channel) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="发送时间">
                {formatDateText(detailRecord?.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label="完成时间">
                {formatDateText(detailRecord?.completed_at)}
              </Descriptions.Item>
              <Descriptions.Item label="总数">{detailRecord?.total_count ?? 0}</Descriptions.Item>
              <Descriptions.Item label="结果">
                <Space size={4}>
                  <Tag color="green">成功 {detailRecord?.success_count ?? 0}</Tag>
                  <Tag color="red">失败 {detailRecord?.failed_count ?? 0}</Tag>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="消息内容" span={2}>
                <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {detailRecord?.message || '-'}
                </Paragraph>
              </Descriptions.Item>
            </Descriptions>

            <Table<BroadcastRecipientLogItem>
              rowKey="id"
              loading={detailLoading}
              dataSource={detailItems}
              pagination={{
                current: detailPage,
                pageSize: detailPageSize,
                total: detailTotal,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条接收记录`,
                onChange: (page, pageSize) => {
                  setDetailPage(page);
                  setDetailPageSize(pageSize);
                },
              }}
              columns={[
                {
                  title: '接收者',
                  width: 220,
                  render: (_, record) => (
                    <Space direction="vertical" size={0}>
                      <Text>{record.username ? `@${record.username}` : record.recipient_name || '-'}</Text>
                      <Text type="secondary">TG: {record.telegram_id}</Text>
                    </Space>
                  ),
                },
                {
                  title: '状态',
                  dataIndex: 'delivery_status',
                  width: 100,
                  render: (value: BroadcastRecipientLogItem['delivery_status']) => renderStatusTag(value),
                },
                {
                  title: '实际通道',
                  dataIndex: 'channel_used',
                  width: 160,
                  render: (value: string) => <Tag color="purple">{renderChannelLabel(value)}</Tag>,
                },
                {
                  title: '失败原因',
                  dataIndex: 'error_message',
                  render: (value: string | null) => value || '-',
                },
                {
                  title: '发送时间',
                  dataIndex: 'sent_at',
                  width: 180,
                  render: (value: string | null) => formatDateText(value),
                },
              ]}
            />
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default Broadcast;
